import { Card, CardContent } from '@/components/ui/card';
import { type ChartConfig } from '@/components/ui/chart';
import { DateRangePicker } from '@/components/ui/date-range-picker';
import { TabsContent } from '@/components/ui/tabs';
import { createFileRoute } from '@tanstack/react-router';
import { format, subWeeks } from 'date-fns';
import { useMemo, useState } from 'react';
import type { DateRange } from 'react-day-picker';
import { useTranslation } from 'react-i18next';

import { useCurrentOrganization } from '@/components/features/membership/membership-provider';
import { useFormatDate } from '@/hooks/use-format-date';
import { getOrganizationMembersQuery } from '@/queries/getOrganizationMembersQuery';
import {
  getScheduleAnalyticsSummaryQuery,
  getScheduleFeedbacksQuery,
  getScheduleMemberPerformanceQuery,
  getScheduleSatisfactionTrendQuery,
  type AnalyticsFilters,
} from '@/queries/getScheduleAnalyticsQuery';
import { useQueries } from '@tanstack/react-query';

import {
  ChartSkeleton,
  CommentsSection,
  CommentsSectionSkeleton,
  MemberPerformanceChart,
  SatisfactionTrendChart,
  SummaryCards,
  SummaryCardsSkeleton,
} from './-parts';

export const Route = createFileRoute(
  '/_authenticated/schedule/$scheduleId/analytics'
)({
  component: RouteComponent,
});

type Granularity = 'weekly' | 'monthly';

function RouteComponent() {
  const { t } = useTranslation();
  const { formatWithPreset } = useFormatDate();
  const { scheduleId } = Route.useParams();
  const { organizationId } = useCurrentOrganization();

  // Create chart config with i18n labels
  const chartConfig = {
    avgScore: {
      label: t(
        'authenticated.schedule.details.analytics.charts.config.avgScore'
      ),
      color: 'hsl(210, 100%, 56%)', // Bright blue
    },
    feedbackCount: {
      label: t(
        'authenticated.schedule.details.analytics.charts.config.feedbackCount'
      ),
      color: 'hsl(280, 100%, 70%)', // Bright purple
    },
    count: {
      label: t('authenticated.schedule.details.analytics.charts.config.count'),
      color: 'hsl(160, 100%, 45%)', // Bright teal/green
    },
    responseRate: {
      label: t(
        'authenticated.schedule.details.analytics.charts.config.responseRate'
      ),
      color: 'hsl(45, 100%, 55%)', // Bright orange/yellow
    },
    memberName: {
      label: t(
        'authenticated.schedule.details.analytics.charts.config.memberName'
      ),
      color: 'hsl(320, 100%, 65%)', // Bright pink/magenta
    },
  } satisfies ChartConfig;

  // Date range state - simplified without granularity dependency
  const [dateRange, setDateRange] = useState<DateRange | undefined>({
    from: subWeeks(new Date(), 12), // Default to 12 weeks
    to: new Date(),
  });

  // Granularity state - only for satisfaction trend chart
  const [satisfactionGranularity, setSatisfactionGranularity] =
    useState<Granularity>('weekly');

  // Prepare filters for API calls
  const filters: AnalyticsFilters = {
    startDate: dateRange?.from
      ? format(dateRange.from, 'yyyy-MM-dd')
      : format(subWeeks(new Date(), 12), 'yyyy-MM-dd'),
    endDate: dateRange?.to
      ? format(dateRange.to, 'yyyy-MM-dd')
      : format(new Date(), 'yyyy-MM-dd'),
  };

  // Fetch analytics data
  const [
    { data: summaryData, isLoading: summaryLoading, error: summaryError },
    { data: satisfactionTrendData, isLoading: trendLoading, error: trendError },
    {
      data: memberPerformanceData,
      isLoading: memberLoading,
      error: memberError,
    },
    { data: feedbacksData, isLoading: feedbackLoading, error: feedbackError },
    { data: orgMembersData, isLoading: membersLoading, error: membersError },
  ] = useQueries({
    queries: [
      getScheduleAnalyticsSummaryQuery(organizationId, scheduleId, filters),
      getScheduleSatisfactionTrendQuery(
        organizationId,
        scheduleId,
        filters,
        satisfactionGranularity
      ),
      getScheduleMemberPerformanceQuery(organizationId, scheduleId, filters),
      getScheduleFeedbacksQuery(organizationId, scheduleId, filters),
      getOrganizationMembersQuery({ organizationId }),
    ],
  });

  // Extract data or use fallbacks
  const summary = summaryData?.summary;
  const satisfactionTrend = satisfactionTrendData?.trends || [];
  const memberPerformance = memberPerformanceData?.performance || [];

  // Format period labels for display - only used by satisfaction trend
  const formatPeriodLabel = (period: string) => {
    if (satisfactionGranularity === 'weekly') {
      const match = period.match(/^(\d{4})-W(\d{2})$/);
      if (match) {
        const [, year, week] = match;
        const date = new Date(parseInt(year), 0, parseInt(week) * 7);
        return formatWithPreset(date, 'week_year');
      }
    } else {
      const match = period.match(/^(\d{4})-(\d{2})$/);
      if (match) {
        const [, year, month] = match;
        const date = new Date(parseInt(year), parseInt(month) - 1);
        return formatWithPreset(date, 'month_year');
      }
    }
    return period;
  };

  // Extract feedbacks and members
  const feedbacks = feedbacksData?.feedbacks || [];
  const members = orgMembersData || [];
  const membersById = useMemo(() => {
    const map: Record<string, string> = {};
    for (const m of members) {
      map[m.memberId] = m.displayName;
    }
    return map;
  }, [members]);

  const comments = useMemo(
    () =>
      feedbacks
        .filter((f) => !!f.text)
        .map((f) => {
          const name = membersById[f.memberId] ?? '—';
          return {
            id: f.feedbackId ?? `${f.memberId}-${f.rotationId}`,
            memberName: name,
            rating: Math.round(f.score ?? 0),
            date: new Date(f.createdAt ?? Date.now()),
            content: f.text as string,
          };
        }),
    [feedbacks, membersById]
  );

  // Loading and error states
  const isLoading =
    summaryLoading ||
    trendLoading ||
    memberLoading ||
    feedbackLoading ||
    membersLoading;
  const hasError =
    summaryError || trendError || memberError || feedbackError || membersError;

  // Error state
  if (hasError) {
    return (
      <TabsContent value="analytics" className="space-y-6">
        <Card>
          <CardContent className="p-6">
            <div className="text-center text-red-600">
              <p>{t('authenticated.schedule.details.analytics.error')}</p>
            </div>
          </CardContent>
        </Card>
      </TabsContent>
    );
  }

  // Use real data with fallbacks
  const totalFeedbacks = summary?.totalFeedbacks || 0;
  const avgSatisfaction = summary?.avgSatisfaction?.toFixed(1) || '0.0';
  const avgResponseRate = Math.round(summary?.avgResponseRate || 0);

  return (
    <TabsContent value="analytics" className="space-y-6 min-w-0">
      {/* Controls */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 mb-6 min-w-0">
        {/* Date Range Picker */}
        <div className="w-full sm:w-auto min-w-0 flex items-center gap-2">
          <span className="hidden sm:inline text-sm font-medium flex-shrink-0">
            {t('authenticated.schedule.details.analytics.dateRange')}:
          </span>
          <DateRangePicker
            dateRange={dateRange}
            onDateRangeChange={setDateRange}
            className="w-full sm:w-auto"
          />
        </div>
      </div>

      {/* Summary Cards */}
      <div className="min-w-0">
        {isLoading ? (
          <SummaryCardsSkeleton />
        ) : (
          <SummaryCards
            totalFeedbacks={totalFeedbacks}
            avgSatisfaction={avgSatisfaction}
            avgResponseRate={avgResponseRate}
            activeMembers={memberPerformance.length}
          />
        )}
      </div>

      {/* Charts Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 min-w-0">
        {/* Satisfaction Trend */}
        <div className="min-w-0">
          {isLoading ? (
            <ChartSkeleton />
          ) : (
            <SatisfactionTrendChart
              data={satisfactionTrend}
              granularity={satisfactionGranularity}
              onGranularityChange={setSatisfactionGranularity}
              formatPeriodLabel={formatPeriodLabel}
              chartConfig={chartConfig}
            />
          )}
        </div>

        {/* Member Performance */}
        <div className="min-w-0">
          {isLoading ? (
            <ChartSkeleton />
          ) : (
            <MemberPerformanceChart
              data={memberPerformance}
              chartConfig={chartConfig}
            />
          )}
        </div>
      </div>

      {/* Comments Section */}
      <div className="min-w-0">
        {isLoading ? (
          <CommentsSectionSkeleton />
        ) : (
          <CommentsSection comments={comments} />
        )}
      </div>
    </TabsContent>
  );
}
